import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { defaultContainerSize, mq } from '../../../styles/mixins';
import { THEME } from '../../../common/consts/theme';
import ButtonWithGradientHover from '../../../styledComponents/ButtonWithGradientHover';
import { Header2, ParagraphS } from '../../core/Typography/Typography';

type NewsletterAgreementType = {
  isVisible: boolean;
};

export const NewsletterWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.light_grey};
  position: relative;
  width: 100%;
  z-index: 10;
  overflow-x: hidden;
  padding-top: 4rem;
  padding-bottom: 5rem;

  ${mq.xLarge(css`
    padding-top: 5.5rem;
    padding-bottom: 8.5rem;
  `)};

  label {
    color: ${({ theme }) => theme.colors.black};
  }
`;

export const NewsletterInner = styled.div`
  ${defaultContainerSize};
`;

export const NewsletterGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(5, auto);

  ${mq.xLarge(css`
    grid-template-columns: [col1]auto [col2]auto;
    grid-template-rows: [row1]auto [row2]auto [row3]auto [row4]auto;
  `)};
`;

export const NewsletterTitle = styled.h3`
  grid-row: 2/3;
  ${Header2};
  color: ${({ theme }) => theme.colors.core_black};
  margin-top: 0;
  margin-bottom: 2rem;

  ${mq.medium(css`
    font-size: 3rem;
    line-height: 3.5rem;
    letter-spacing: -0.0125rem;
    max-width: 46.625rem;
    margin-bottom: 1.5rem;
  `)};

  ${mq.xLarge(css`
    grid-row: 1/2;
    grid-column: 1/3;
  `)};
`;

export const NewsletterAboutFirst = styled.p`
  ${ParagraphS};
  margin-top: 0;
  grid-row: 3/4;
  color: ${({ theme }) => theme.colors.black};
  letter-spacing: 0.0125rem;
  margin-bottom: 2rem;

  ${mq.large(css`
    max-width: 51.5rem;
    margin-bottom: 2.5rem;
  `)};

  ${mq.xLarge(css`
    max-width: 37.5rem;
    max-height: 4rem;
    grid-column: col1;
    grid-row: row2/row3;
    margin: 0;
  `)};
`;

export const NewsletterAboutSecond = styled.p`
  margin-top: 0;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 0.875rem;
  font-family: ${({ theme }) => theme.font.questrial};
  color: ${({ theme }) => theme.colors.darker_grey};

  ${mq.xLarge(css`
    grid-column: col1;
    grid-row: row4;
  `)};
`;

export const NewsletterImage = styled.div`
  max-width: 12.5rem;
  grid-row: 1 / 2;
  margin-bottom: 2.5rem;

  ${mq.medium(css`
    max-width: 18.75rem;
    max-height: 12.0625rem;
  `)};

  ${mq.xLarge(css`
    max-width: 25rem;
    min-height: 15.625rem;
    grid-row: 1 / 4;
    grid-column: col2;
    justify-self: center;
    align-self: center;
    margin-top: 3.125rem;
    margin-left: 1.5rem;
  `)};

  svg {
    ${mq.medium(css`
      width: 18.75rem;
      height: 12.0625rem;
    `)};

    ${mq.xLarge(css`
      width: 25rem;
      height: 15.625rem;
    `)};
  }
`;

export const NewsletterFormContainer = styled.div`
  ${mq.xLarge(css`
    margin-top: 4rem;
    grid-column: col1/col2;
    grid-row: row3/row4;
  `)};
`;

export const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;

  ${mq.medium(css`
    margin-top: 0.5rem;
  `)};
`;

export const CheckboxContainerButton = styled.button`
  margin: 1.375rem 0 0;
  padding: 0;
  background: transparent;
  border: none;
  align-self: flex-start;

  ${mq.xLarge(css`
    margin: 0;
  `)};
`;

export const NewsletterFullAgreement = styled.div<NewsletterAgreementType>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  border: 0.0625rem solid #e3e3e3;
  background: ${({ theme }) => theme.colors.core_white};
  border-radius: 0.125rem;
  padding: 1rem;
  z-index: 40;
  visibility: ${({ theme, isVisible }) => (isVisible ? 'visible' : 'hidden')};
  margin-right: 1rem;
  margin-left: 1rem;

  ${mq.medium(css`
    width: 42rem;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0 auto;
    padding: 2rem;
  `)};

  ${mq.xLarge(css`
    max-width: 35rem;
    position: static;
    transform: none;
    grid-column: 2;
    grid-row: 2 / 4;
  `)};

  @media (min-width: 1250px) {
    max-width: 38rem;
  }

  @media (min-width: 1300px) {
    max-width: 42.8125rem;
  }
`;

type ArrowType = {
  positionX?: number;
  positionXlg?: number;
};

export const Arrow = styled.div<ArrowType>`
  display: none;
  position: absolute;
  width: 1.375rem;
  height: 1.375rem;
  background-color: ${({ theme }) => theme.colors.core_white};
  transform: rotate(45deg);

  ${({ positionX, positionXlg }) =>
    positionX &&
    css`
      ${mq.medium(css`
        right: ${positionX}px;
      `)};

      ${mq.large(css`
        right: ${positionXlg}px;
      `)};
    `};

  ${mq.medium(css`
    display: block;
    border: 0.0625rem solid #e3e3e3;
    border-left: none;
    border-top: none;
    margin-top: 21px;
  `)};

  ${mq.xLarge(css`
    border: 0.0625rem solid #e3e3e3;
    border-right: none;
    border-top: none;
    right: unset;
    margin-left: -43px;
    margin-top: 252px;
  `)};
`;

export const AgreementParagraph = styled.p`
  font-family: ${({ theme }) => theme.font.nunito};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.5rem;
  letter-spacing: 0.0125rem;
  color: ${({ theme }) => theme.colors.black};
  margin-top: 0;

  &:last-child {
    margin-bottom: 0;
  }

  > a {
    color: ${({ theme }) => theme.colors.black};
  }
`;

export const NewsletterModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2.5rem 1.5rem;

  ${mq.medium(css`
    padding: 3.5rem 2.25rem 5rem 2.25rem;
  `)};

  ${mq.large(css`
    padding: 3.5rem 6.875rem 5rem 6.875rem;
  `)};

  svg {
    margin: 0 auto 2rem auto;
  }
`;

export const NewsletterModalButton = styled.button`
  font-size: 1.125rem;
  line-height: 2rem;
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.font.nunito};
  font-weight: bold;
  border: none;
  background: transparent;
  padding: 0;
  margin-bottom: 0.625rem;
`;

export const NewsletterModalOverlay = styled.div<NewsletterAgreementType>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  visibility: ${({ theme, isVisible }) => (isVisible ? 'visible' : 'hidden')};
  background: rgba(${THEME.colors.core_black}, 0.65);
`;

export const NewsletterSubmitButton = styled(ButtonWithGradientHover)`
  margin: 0 0 1rem;
  font-family: ${({ theme }) => theme.font.questrial};

  ${mq.medium(css`
    padding: 0.75rem 1.5rem;
    max-width: 16.875rem;
    margin-bottom: 2.5rem;
  `)};

  ${mq.xLarge(css`
    max-width: 16.875rem;
    margin-bottom: 2.625rem;
  `)};
`;

export const NewsletterModalButtonAccept = styled(NewsletterModalButton)`
  margin-bottom: 2rem;
`;

export const NewsletterModalTitle = styled.h2`
  font-size: 2rem;
  line-height: 2.5rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.core_black};
  font-family: ${({ theme }) => theme.font.questrial};
  text-align: center;
  margin: 0 0 1rem 0;

  ${mq.medium(css`
    font-size: 2.5rem;
    line-height: 3.5rem;
    letter-spacing: -0.0125rem;
  `)};
`;

export const NewsletterModalText = styled.p`
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.5rem;
  letter-spacing: 0.0125rem;
  font-family: ${({ theme }) => theme.font.nunito};
  text-align: center;
  color: ${({ theme }) => theme.colors.black};
  margin: 0 0 2.5rem 0;

  ${mq.medium(css`
    font-size: 1.25rem;
    line-height: 2rem;
    margin: 0 0 2rem 0;
  `)};
`;

export const NewsletterModalTextSecond = styled(NewsletterModalText)`
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 0.875rem;
  color: ${({ theme }) => theme.colors.darker_grey};
  margin: 0;

  ${mq.medium(css`
    font-size: 0.75rem;
    line-height: 0.875rem;
    margin: 0;
  `)};
`;
